// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricCardStatusType, TechnicalMetricType, TechnicalMetricValues, MetricBottomSummary, ChartDataWithMetricType, ChartData } from '@lfx/key-projects/key-projects.model';
import { ChartType, BaseMetricConfig, BaseMetricEntity, ScoreLevel } from './base-metric.entity';
import { CommitActivities } from './commit-activities.entity';
import { Maintainers } from './maintainers.entity';
import { Contributors } from './contributors.entity';
import { PullRequestsOpened } from './pull-requests-opened.entity';
import { AverageTimeToMergePrs } from './avg-time-to-merge-prs.entity';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';

export interface TechnicalMetricCardData {
  title: string;
  titleDescription: string;
  chartType: ChartType;
  firstDate: string;
  lastDate: string;
  bottomSummary?: MetricBottomSummary;
  metricType: TechnicalMetricType;
  data?: number[];
  multiSeriesData?: {
    data: number[];
    label?: string;
    dashArray?: number[];
  }[];
  xAxisLabels?: string[];
  iconType?: 'eyes' | undefined;
}

interface MetricEntityClass {
  METRIC_TYPE: string;
  getMetricConfig(): BaseMetricConfig;
  getBottomSummary(value?: number | string | { percent: string | null; speed: 'faster' | 'slower' | 'same' | null }, level?: ScoreLevel): MetricBottomSummary;
}

const METRIC_ENTITY_MAPPING: Record<string, MetricEntityClass> = {
  [CommitActivities.METRIC_TYPE]: CommitActivities,
  [Maintainers.METRIC_TYPE]: Maintainers,
  [Contributors.METRIC_TYPE]: Contributors,
  [PullRequestsOpened.METRIC_TYPE]: PullRequestsOpened,
  [AverageTimeToMergePrs.METRIC_TYPE]: AverageTimeToMergePrs,
};


export interface TechnicalInfluenceComponentConfig {
  titleText: string;
  influenceType: string;
  noDataDescription: string;
  loadingDescription: string;
  loadingStatusType: MetricCardStatusType;
  loadingChartType: ChartType;
  multiSeriesData: {
    ourOrganization: {
      label: string;
    };
    others: {
      label: string;
      dashArray: number[];
    };
  };
}

/**
 * TechnicalInfluence Entity - Core domain entity for technical influence business rules
 *
 * Individual metrics contribute 0-3 points each:
 * - maintainers, contributors, commit_activities
 * - pull_requests_opened, avg_time_to_merge_prs
 */
export class TechnicalInfluence {
  static readonly COMPONENT_CONFIG: TechnicalInfluenceComponentConfig = {
    titleText: 'Our Technical Influence',
    influenceType: 'ecosystem',
    noDataDescription: 'This metric does not apply to this project because this project has no data of this type.',
    loadingDescription: 'Loading...',
    loadingStatusType: 'info',
    loadingChartType: 'loading' as ChartType,
    multiSeriesData: {
      ourOrganization: {
        label: 'Our Organization'
      },
      others: {
        label: 'Others',
        dashArray: [2, 2]
      }
    }
  };

  private static determineDataPresenceForMetric(
    metricType: TechnicalMetricType,
    metricDetails: ChartDataWithMetricType,
    maintainersCount: number
  ): boolean {
    if (metricType === 'maintainers') {
      return maintainersCount > 0;
    }
    return metricDetails.chartData !== undefined && metricDetails.chartData.length > 0;
  }

  private static calculateIconTypeForMetric(
    metricType: TechnicalMetricType,
    hasData: boolean,
    maintainersCount: number,
    chartData: ChartData[] | undefined
  ): 'eyes' | undefined {
    if (metricType === 'maintainers') {
      return maintainersCount === 0 ? 'eyes' : undefined;
    }

    if (!hasData) {
      return 'eyes';
    }

    if (chartData) {
      return BaseMetricEntity.getIconTypeForChartData(chartData);
    }

    return undefined;
  }

  private static buildChartDataObject(
    baseCard: Partial<TechnicalMetricCardData>,
    metricType: TechnicalMetricType,
    chartData: ChartData[]
  ): TechnicalMetricCardData {
    const xAxisLabels = KeyProjectsHelper.generateXAxisLabels(chartData);

    return {
      ...baseCard as TechnicalMetricCardData,
      metricType,
      data: chartData.map(d => d.metricCount),
      xAxisLabels,
      multiSeriesData: [
        {
          data: chartData.map(d => d.metricCount),
          label: 'Our Organization'
        },
        {
          data: chartData.map(d => d.othersCount),
          label: 'Others',
          dashArray: [2, 2]
        },
      ]
    };
  }

  private static buildNoDataObject(
    baseCard: Partial<TechnicalMetricCardData>,
    metricType: TechnicalMetricType
  ): TechnicalMetricCardData {
    return {
      ...baseCard as TechnicalMetricCardData,
      metricType,
      data: [],
      multiSeriesData: [],
      xAxisLabels: []
    };
  }

  static transformToCardData(
    metricDetails: ChartDataWithMetricType,
    techInfluenceOverviewValues: TechnicalMetricValues
  ): TechnicalMetricCardData {
    const metricType = metricDetails.metricType as TechnicalMetricType;
    const config = this.getConfig(metricType);
    const maintainersCount = techInfluenceOverviewValues?.maintainersCount ?? 0;

    const hasData = this.determineDataPresenceForMetric(metricType, metricDetails, maintainersCount);

    const iconType = this.calculateIconTypeForMetric(metricType, hasData, maintainersCount, metricDetails.chartData);

    const actualChartType = hasData ? config.display.chartType : 'no-data';

    const metricDataMap = {
      'commit_activities': {
        value: techInfluenceOverviewValues?.commitsPercent,
        level: techInfluenceOverviewValues?.commitsLevel
      },
      'pull_requests_opened': {
        value: techInfluenceOverviewValues?.prsOpenedPercent,
        level: techInfluenceOverviewValues?.prsOpenedLevel
      },
      'avg_time_to_merge_prs': {
        value: techInfluenceOverviewValues?.averageMergedTime,
        level: techInfluenceOverviewValues?.avgMergeTimeLevel
      },
      'maintainers': {
        value: techInfluenceOverviewValues?.maintainersCount,
        level: techInfluenceOverviewValues?.maintainersLevel
      },
      'contributors': {
        value: techInfluenceOverviewValues?.contributorsPercent,
        level: techInfluenceOverviewValues?.contributorsLevel
      }
    }
    const metricData = metricDataMap[metricType];
    const latestValue = metricData?.value;
    const level = metricData?.level as ScoreLevel | undefined;
    const bottomSummary = this.getBottomSummary(metricType, latestValue, level);

    const baseCard = {
      title: config.display.displayName,
      titleDescription: BaseMetricEntity.generateRulesHtml(config.display.tooltip),
      chartType: actualChartType,
      firstDate: hasData ? metricDetails.firstDate : 'No data',
      lastDate: hasData ? metricDetails.lastDate : 'No data',
      bottomSummary,
      iconType
    };

    if (hasData) {
      return this.buildChartDataObject(baseCard, metricType, metricDetails.chartData);
    } else {
      return this.buildNoDataObject(baseCard, metricType);
    }
  }

  static getConfig(metricType: TechnicalMetricType): BaseMetricConfig {
    const entityClass = METRIC_ENTITY_MAPPING[metricType]

    return entityClass.getMetricConfig();
  }

  static getBottomSummary(metricType: TechnicalMetricType, value?: string | number | { percent: string | null; speed: 'faster' | 'slower' | 'same' | null }, level?: ScoreLevel): MetricBottomSummary {
    const entityClass = METRIC_ENTITY_MAPPING[metricType];
    if (!entityClass) {
      return undefined;
    }

    return entityClass.getBottomSummary(value, level);
  }

  static createSkeletonCards(): TechnicalMetricCardData[] {
    const technicalMetrics = this.getTechnicalMetricTypes();
    const styles = BaseMetricEntity.getNullValueStyles();

    return technicalMetrics.map(metricType => {
      return {
        title: this.getConfig(metricType).display.displayName,
        titleDescription: BaseMetricEntity.generateRulesHtml(this.getConfig(metricType).display.tooltip),
        chartType: this.COMPONENT_CONFIG.loadingChartType,
        data: [],
        multiSeriesData: [],
        firstDate: '',
        lastDate: '',
        metricType: metricType,
        bottomSummary: {
          title: '',
          description: this.COMPONENT_CONFIG.loadingDescription,
          icon: styles.icon,
          iconColor: styles.iconColor,
          borderColor: styles.borderColor
        }
      };
    });
  }

  /**
   * Get all technical metric types
   */
  static getTechnicalMetricTypes(): readonly TechnicalMetricType[] {
    return [
      'maintainers',
      'contributors',
      'commit_activities',
      'pull_requests_opened',
      'avg_time_to_merge_prs'
    ] as const;
  }

}
